import ProgressBar from '_components/ProgressBar';
import LFCButton from '_components/inputs/LFCButton';
import {useAuthUser} from '_contexts/AuthUserProvider';
import {isLinkwizEmployeeEmail} from '_logics/LFCAuthUtil';
import {DeviceInfo, getDeviceInfoList} from '_logics/LFCDeviceInfoUtil';
import {getSourceCollectors} from '_logics/LFCSourceUtil';
import GenericTemplate from '_templates/GenericTemplate';
import {useSnackbar} from 'notistack';
import {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Box, Button, Grid} from '@mui/material';
import {DataGridPro, GridColumns, GridRenderCellParams} from '@mui/x-data-grid-pro';

import DeleteHistoryDialog from './_components/DeleteHistoryDialog';
import DeviceInfoDialog from './_components/DeviceInfoDialog';
import ProfileEditor from './_components/ProfileEditor';

type DeviceRow = {id: string} & DeviceInfo;

const DeviceSettingsPage = () => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const authUser = useAuthUser();
  const canDeleteHistory =
    authUser?.emailVerified === true && isLinkwizEmployeeEmail(authUser.email);
  const [startProcess, setStartProcess] = useState(false);
  const [rows, setRows] = useState<DeviceRow[]>([]);
  const [editTarget, setEditTarget] = useState<DeviceRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeviceRow | null>(null);
  const [profileEditorOpen, setProfileEditorOpen] = useState(false);

  const load = useCallback(() => {
    setStartProcess(true);
    Promise.all([getSourceCollectors(), getDeviceInfoList({snack: enqueueSnackbar, t})])
      .then(([collectors, infos]) => {
        const infoMap = new Map(infos.map(i => [i.device_name, i]));
        setRows(
          collectors.map(name => ({
            id: name,
            device_name: name,
            display_name: infoMap.get(name)?.display_name ?? null,
            robot_manufacturer: infoMap.get(name)?.robot_manufacturer ?? null,
            robot_model: infoMap.get(name)?.robot_model ?? null,
            installation_location: infoMap.get(name)?.installation_location ?? null,
            department: infoMap.get(name)?.department ?? null,
            manager: infoMap.get(name)?.manager ?? null,
            memo: infoMap.get(name)?.memo ?? null,
            updated_at: infoMap.get(name)?.updated_at ?? null
          }))
        );
      })
      .catch(e => {
        console.error(e);
        enqueueSnackbar(t('message.エラーが発生しました。'), {variant: 'error'});
      })
      .finally(() => setStartProcess(false));
  }, [enqueueSnackbar, t]);

  useEffect(() => {
    load();
  }, [load]);

  const columns: GridColumns = [
    {field: 'device_name', headerName: t('端末名'), description: t('端末名'), width: 180},
    {field: 'display_name', headerName: t('表示名'), description: t('表示名'), width: 160},
    {
      field: 'robot_manufacturer',
      headerName: t('ロボットメーカー'),
      description: t('ロボットメーカー'),
      width: 140
    },
    {field: 'robot_model', headerName: t('機種'), description: t('機種'), width: 120},
    {
      field: 'installation_location',
      headerName: t('設置場所'),
      description: t('設置場所'),
      width: 140
    },
    {field: 'department', headerName: t('管理部署'), description: t('管理部署'), width: 120},
    {field: 'manager', headerName: t('管理担当者'), description: t('管理担当者'), width: 120},
    {field: 'updated_at', headerName: t('最終更新'), description: t('最終更新'), width: 160},
    {
      field: 'edit',
      headerName: '',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      renderCell: (prms: GridRenderCellParams<unknown, DeviceRow>) => (
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => setEditTarget(prms.row)}
        >
          {t('編集')}
        </Button>
      )
    },
    ...(canDeleteHistory
      ? [
          {
            field: 'delete',
            headerName: '',
            width: 120,
            align: 'center' as const,
            headerAlign: 'center' as const,
            sortable: false,
            filterable: false,
            renderCell: (prms: GridRenderCellParams<unknown, DeviceRow>) => (
              <Button
                variant="contained"
                color="secondary"
                size="small"
                onClick={() => setDeleteTarget(prms.row)}
              >
                {t('履歴データ削除')}
              </Button>
            )
          }
        ]
      : [])
  ];

  return (
    <GenericTemplate title={t('端末情報設定')}>
      <ProgressBar startProcess={startProcess} />
      <Grid
        container
        spacing={2}
        rowSpacing={2}
        alignItems="flex-start"
        justifyContent="flex-start"
      >
        <Grid item xs={12}>
          <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2, width: '100%'}}>
            <LFCButton color="primary" onClick={() => load()}>
              {t('message.最新の情報に更新')}
            </LFCButton>
            <LFCButton
              color="primary"
              onClick={() => setProfileEditorOpen(true)}
              style={{marginLeft: 'auto'}}
            >
              {t('accum.profiles')}
            </LFCButton>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box style={{height: '75vh'}}>
            <DataGridPro
              columns={columns}
              rows={rows}
              density="compact"
              showCellRightBorder
              showColumnRightBorder
              disableSelectionOnClick
              initialState={{
                pinnedColumns: {
                  left: ['device_name'],
                  right: canDeleteHistory ? ['edit', 'delete'] : ['edit']
                }
              }}
            />
          </Box>
        </Grid>
      </Grid>

      {editTarget && (
        <DeviceInfoDialog
          open
          row={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            load();
          }}
        />
      )}
      {profileEditorOpen && <ProfileEditor onClose={() => setProfileEditorOpen(false)} />}
      {canDeleteHistory && deleteTarget && (
        <DeleteHistoryDialog
          open
          deviceName={deleteTarget.device_name}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            setDeleteTarget(null);
            load();
          }}
        />
      )}
    </GenericTemplate>
  );
};

export default DeviceSettingsPage;
